import { useEffect } from 'react';

export interface UseEditorKeyboardShortcutsArgs {
  onUndo: () => void;
  onRedo: () => void;
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
}

const isEditableTarget = (target: EventTarget | null): boolean => {
  const tag = (target as HTMLElement | null)?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
};

const hasTextSelection = (): boolean => {
  if (typeof window === 'undefined') return false;
  const sel = window.getSelection();
  return Boolean(sel && sel.toString().length > 0);
};

/**
 * Wires the editor's keyboard shortcuts (Ctrl/Cmd + Z/Y/C/X/V) to the
 * provided handlers. Lives in a hook so the page itself stays smaller
 * and the shortcut policy (e.g. "don't hijack copy when text is
 * selected") is exercised by its own tests.
 */
export const useEditorKeyboardShortcuts = ({
  onUndo,
  onRedo,
  onCopy,
  onCut,
  onPaste,
}: UseEditorKeyboardShortcutsArgs): void => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod) return;
      if (isEditableTarget(e.target)) return;

      // Don't hijack Ctrl+C / Ctrl+X when the user has plain text selected.
      if ((e.key === 'c' || e.key === 'x') && hasTextSelection()) return;

      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        onUndo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        onRedo();
      } else if (e.key === 'c') {
        e.preventDefault();
        onCopy();
      } else if (e.key === 'x') {
        e.preventDefault();
        onCut();
      } else if (e.key === 'v') {
        e.preventDefault();
        onPaste();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onUndo, onRedo, onCopy, onCut, onPaste]);
};
